import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import Modal from '../components/Modal';
import { Plus, Search, Folder, Tag, Layers, FolderPlus, X, Upload, Archive } from 'lucide-react';

export default function ModelsPage() {
  const [models, setModels] = useState([]);
  const [folders, setFolders] = useState([]);
  const [flatFolders, setFlatFolders] = useState([]);
  const [tags, setTags] = useState([]);
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showFolderCreate, setShowFolderCreate] = useState(false);
  const [showTagCreate, setShowTagCreate] = useState(false);
  const [newModel, setNewModel] = useState({ name: '', description: '', folder_id: '', tags: [] });
  const [newFolder, setNewFolder] = useState({ name: '', parent_id: '' });
  const [newTag, setNewTag] = useState({ name: '', color: '#6366f1' });
  const [showMassImport, setShowMassImport] = useState(false);
  const [massImportFile, setMassImportFile] = useState(null);
  const [massImportDragging, setMassImportDragging] = useState(false);
  const [massImportLoading, setMassImportLoading] = useState(false);
  const [massImportResult, setMassImportResult] = useState(null);
  const massImportInputRef = useRef(null);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const loadData = useCallback(() => {
    const params = {};
    if (searchParams.get('folder')) params.folder_id = searchParams.get('folder');
    if (searchParams.get('tag')) params.tag = searchParams.get('tag');
    if (searchParams.get('filter')) params.smart_filter = searchParams.get('filter');
    if (search) params.search = search;

    api.get('/models', { params }).then(r => setModels(r.data.models));
    api.get('/folders').then(r => { setFolders(r.data.folders); setFlatFolders(r.data.flat); });
    api.get('/tags').then(r => setTags(r.data.tags));
  }, [search, searchParams]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleCreate = async () => {
    if (!newModel.name) return;
    await api.post('/models', { ...newModel, folder_id: newModel.folder_id || null });
    setShowCreate(false);
    setNewModel({ name: '', description: '', folder_id: '', tags: [] });
    loadData();
  };

  const handleCreateFolder = async () => {
    if (!newFolder.name) return;
    await api.post('/folders', { ...newFolder, parent_id: newFolder.parent_id || null });
    setShowFolderCreate(false);
    setNewFolder({ name: '', parent_id: '' });
    loadData();
  };

  const handleCreateTag = async () => {
    if (!newTag.name) return;
    await api.post('/tags', newTag);
    setShowTagCreate(false);
    setNewTag({ name: '', color: '#6366f1' });
    loadData();
  };

  const smartFilters = [
    { key: 'recent', label: 'Recently Added', icon: '🕐' },
    { key: 'needs_gcode', label: 'Needs G-code', icon: '⚠️' },
    { key: 'in_queue', label: 'In Print Queue', icon: '🖨️' },
    { key: 'incomplete', label: 'Incomplete Prints', icon: '📋' },
  ];

  const clearFilters = () => {
    setSearchParams({});
    setActiveFilter(null);
  };

  const handleMassImportDrop = (e) => {
    e.preventDefault();
    setMassImportDragging(false);
    const file = e.dataTransfer.files[0];
    if (file && file.name.endsWith('.zip')) setMassImportFile(file);
  };

  const handleMassImportSubmit = async () => {
    if (!massImportFile) return;
    setMassImportLoading(true);
    setMassImportResult(null);
    try {
      const formData = new FormData();
      formData.append('zipfile', massImportFile);
      const res = await api.post('/models/mass-import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000,
      });
      setMassImportResult({ success: true, summary: res.data.summary });
      loadData();
    } catch (err) {
      setMassImportResult({ success: false, error: err.response?.data?.error || err.message });
    } finally {
      setMassImportLoading(false);
    }
  };

  const closeMassImport = () => {
    setShowMassImport(false);
    setMassImportFile(null);
    setMassImportResult(null);
    setMassImportLoading(false);
  };

  const FolderTreeItem = ({ folder, depth = 0 }) => (
    <>
      <div
        className={`folder-item ${searchParams.get('folder') === String(folder.id) ? 'active' : ''}`}
        style={{ paddingLeft: `${10 + depth * 16}px` }}
        onClick={() => setSearchParams({ folder: folder.id })}
      >
        <Folder size={14} />
        <span style={{ flex: 1 }}>{folder.name}</span>
        {folder.model_count > 0 && <span className="badge badge-accent">{folder.model_count}</span>}
      </div>
      {folder.children?.map(c => <FolderTreeItem key={c.id} folder={c} depth={depth + 1} />)}
    </>
  );

  return (
    <div className="fade-in">
      <div className="page-header">
        <h1>Models</h1>
        <div className="page-header-actions">
          <button className="btn btn-secondary" onClick={() => setShowFolderCreate(true)}><FolderPlus size={16} /> New Folder</button>
          <button className="btn btn-secondary" onClick={() => setShowTagCreate(true)}><Tag size={16} /> New Tag</button>
          <button className="btn btn-secondary" onClick={() => { setShowMassImport(true); setMassImportFile(null); setMassImportResult(null); }}><Archive size={16} /> Mass Import</button>
          <button className="btn btn-primary" onClick={() => setShowCreate(true)}><Plus size={16} /> New Model</button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '24px' }}>
        {/* Sidebar filters */}
        <div style={{ width: '220px', flexShrink: 0 }}>
          <div style={{ marginBottom: '16px' }}>
            <div style={{ position: 'relative' }}>
              <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
              <input
                placeholder="Search models..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                style={{ paddingLeft: '36px' }}
              />
            </div>
          </div>

          {(searchParams.toString()) && (
            <button className="btn btn-secondary btn-sm" onClick={clearFilters} style={{ width: '100%', marginBottom: '12px', justifyContent: 'center' }}>
              <X size={14} /> Clear Filters
            </button>
          )}

          <div className="nav-section">Smart Filters</div>
          {smartFilters.map(f => (
            <div
              key={f.key}
              className={`folder-item ${searchParams.get('filter') === f.key ? 'active' : ''}`}
              onClick={() => setSearchParams({ filter: f.key })}
            >
              <span>{f.icon}</span> {f.label}
            </div>
          ))}

          <div className="nav-section" style={{ marginTop: '12px' }}>Folders</div>
          <div
            className={`folder-item ${!searchParams.get('folder') && !searchParams.get('filter') && !searchParams.get('tag') ? 'active' : ''}`}
            onClick={clearFilters}
          >
            <Layers size={14} /> All Models
          </div>
          {folders.map(f => <FolderTreeItem key={f.id} folder={f} />)}

          <div className="nav-section" style={{ marginTop: '12px' }}>Tags</div>
          {tags.map(t => (
            <div
              key={t.id}
              className={`folder-item ${searchParams.get('tag') === t.name ? 'active' : ''}`}
              onClick={() => setSearchParams({ tag: t.name })}
            >
              <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: t.color, flexShrink: 0 }} />
              {t.name}
              <span className="badge badge-accent" style={{ marginLeft: 'auto' }}>{t.model_count}</span>
            </div>
          ))}
        </div>

        {/* Model grid */}
        <div style={{ flex: 1 }}>
          {models.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-muted)' }}>
              <Layers size={48} style={{ marginBottom: '16px', opacity: 0.3 }} />
              <p style={{ fontSize: '16px', marginBottom: '8px' }}>No models found</p>
              <p style={{ fontSize: '14px' }}>Create your first model or adjust your filters</p>
            </div>
          ) : (
            <div className="grid grid-3">
              {models.map(m => (
                <div key={m.id} className="card model-card" onClick={() => navigate(`/models/${m.id}`)}>
                  <div className="model-preview" style={m.thumbnail_url ? { padding: 0 } : undefined}>
                    {m.thumbnail_url ? (
                      <img src={m.thumbnail_url} alt={m.name} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'var(--radius) var(--radius) 0 0' }} />
                    ) : (
                      <Layers size={36} style={{ opacity: 0.2 }} />
                    )}
                  </div>
                  <div className="model-info">
                    <div className="model-name">{m.name}</div>
                    <div className="model-meta">
                      <span>{m.part_count} part{m.part_count !== 1 ? 's' : ''}</span>
                      {m.folder_name && <span>📁 {m.folder_name}</span>}
                    </div>
                    {m.tag_names && (
                      <div className="model-tags">
                        {m.tag_names.split(',').map(t => <span key={t} className="tag">{t}</span>)}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Create Model Modal */}
      <Modal isOpen={showCreate} onClose={() => setShowCreate(false)} title="New Model"
        footer={<><button className="btn btn-secondary" onClick={() => setShowCreate(false)}>Cancel</button><button className="btn btn-primary" onClick={handleCreate}>Create</button></>}>
        <div className="form-group">
          <label>Name</label>
          <input value={newModel.name} onChange={e => setNewModel({ ...newModel, name: e.target.value })} placeholder="e.g. Drone Frame V2" autoFocus />
        </div>
        <div className="form-group">
          <label>Description</label>
          <textarea value={newModel.description} onChange={e => setNewModel({ ...newModel, description: e.target.value })} rows={3} placeholder="Optional description..." />
        </div>
        <div className="form-group">
          <label>Folder</label>
          <select value={newModel.folder_id} onChange={e => setNewModel({ ...newModel, folder_id: e.target.value })}>
            <option value="">No folder</option>
            {flatFolders.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label>Tags</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {tags.map(t => (
              <button key={t.id} type="button"
                className={`tag ${newModel.tags.includes(t.id) ? '' : ''}`}
                style={newModel.tags.includes(t.id) ? { background: t.color + '33', color: t.color, border: `1px solid ${t.color}` } : { background: 'var(--bg-tertiary)', color: 'var(--text-muted)', border: '1px solid var(--border)' }}
                onClick={() => {
                  const tags = newModel.tags.includes(t.id) ? newModel.tags.filter(id => id !== t.id) : [...newModel.tags, t.id];
                  setNewModel({ ...newModel, tags });
                }}>
                {t.name}
              </button>
            ))}
          </div>
        </div>
      </Modal>

      {/* Create Folder Modal */}
      <Modal isOpen={showFolderCreate} onClose={() => setShowFolderCreate(false)} title="New Folder"
        footer={<><button className="btn btn-secondary" onClick={() => setShowFolderCreate(false)}>Cancel</button><button className="btn btn-primary" onClick={handleCreateFolder}>Create</button></>}>
        <div className="form-group">
          <label>Folder Name</label>
          <input value={newFolder.name} onChange={e => setNewFolder({ ...newFolder, name: e.target.value })} placeholder="e.g. Drones" autoFocus />
        </div>
        <div className="form-group">
          <label>Parent Folder</label>
          <select value={newFolder.parent_id} onChange={e => setNewFolder({ ...newFolder, parent_id: e.target.value })}>
            <option value="">Root level</option>
            {flatFolders.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
        </div>
      </Modal>

      {/* Create Tag Modal */}
      <Modal isOpen={showTagCreate} onClose={() => setShowTagCreate(false)} title="New Tag"
        footer={<><button className="btn btn-secondary" onClick={() => setShowTagCreate(false)}>Cancel</button><button className="btn btn-primary" onClick={handleCreateTag}>Create</button></>}>
        <div className="form-group">
          <label>Tag Name</label>
          <input value={newTag.name} onChange={e => setNewTag({ ...newTag, name: e.target.value })} placeholder="e.g. drone-parts" autoFocus />
        </div>
        <div className="form-group">
          <label>Color</label>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input type="color" value={newTag.color} onChange={e => setNewTag({ ...newTag, color: e.target.value })} style={{ width: '48px', height: '38px', padding: '2px' }} />
            <input value={newTag.color} onChange={e => setNewTag({ ...newTag, color: e.target.value })} style={{ flex: 1 }} />
          </div>
        </div>
      </Modal>

      {/* Mass Import Modal */}
      <Modal
        isOpen={showMassImport}
        onClose={closeMassImport}
        title="Mass Import"
        footer={
          massImportResult ? (
            <button className="btn btn-primary" onClick={closeMassImport}>Done</button>
          ) : (
            <>
              <button className="btn btn-secondary" onClick={closeMassImport} disabled={massImportLoading}>Cancel</button>
              <button className="btn btn-primary" onClick={handleMassImportSubmit} disabled={!massImportFile || massImportLoading}>
                {massImportLoading ? 'Processing...' : 'Import'}
              </button>
            </>
          )
        }
      >
        {massImportResult ? (
          /* Results view */
          massImportResult.success ? (
            <div>
              <div style={{ color: '#22c55e', fontWeight: 600, fontSize: '15px', marginBottom: '12px' }}>Import complete!</div>
              <div style={{ display: 'flex', gap: '24px', marginBottom: '16px' }}>
                {massImportResult.summary.created > 0 && (
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '28px', fontWeight: 700, color: 'var(--accent)' }}>{massImportResult.summary.created}</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>New models created</div>
                  </div>
                )}
                {massImportResult.summary.merged > 0 && (
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '28px', fontWeight: 700, color: 'var(--accent)' }}>{massImportResult.summary.merged}</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Existing models updated</div>
                  </div>
                )}
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '28px', fontWeight: 700, color: 'var(--accent)' }}>{massImportResult.summary.parts}</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Total parts imported</div>
                </div>
              </div>
              {massImportResult.summary.errors?.length > 0 && (
                <div>
                  <div style={{ color: '#f87171', fontSize: '13px', fontWeight: 600, marginBottom: '6px' }}>Errors ({massImportResult.summary.errors.length}):</div>
                  {massImportResult.summary.errors.map((e, i) => (
                    <div key={i} style={{ fontSize: '12px', color: 'var(--text-muted)', background: 'var(--bg-tertiary)', padding: '6px 10px', borderRadius: '6px', marginBottom: '4px' }}>
                      <strong>{e.model}:</strong> {e.error}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div style={{ color: '#f87171' }}>
              <div style={{ fontWeight: 600, marginBottom: '8px' }}>Import failed</div>
              <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>{massImportResult.error}</div>
            </div>
          )
        ) : massImportLoading ? (
          /* Loading view */
          <div style={{ textAlign: 'center', padding: '32px 0' }}>
            <div style={{ fontSize: '32px', marginBottom: '12px' }}>⏳</div>
            <div style={{ fontWeight: 600, marginBottom: '6px' }}>Processing...</div>
            <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Extracting and importing models. This may take a moment.</div>
          </div>
        ) : (
          /* Upload view */
          <div>
            <div
              onDragOver={e => { e.preventDefault(); setMassImportDragging(true); }}
              onDragLeave={() => setMassImportDragging(false)}
              onDrop={handleMassImportDrop}
              onClick={() => massImportInputRef.current?.click()}
              style={{
                border: `2px dashed ${massImportDragging ? 'var(--accent)' : 'var(--border)'}`,
                borderRadius: '10px',
                padding: '32px',
                textAlign: 'center',
                cursor: 'pointer',
                background: massImportDragging ? 'var(--bg-tertiary)' : 'transparent',
                transition: 'all 0.15s',
                marginBottom: '16px',
              }}
            >
              <Upload size={32} style={{ marginBottom: '10px', opacity: 0.5 }} />
              {massImportFile ? (
                <div>
                  <div style={{ fontWeight: 600 }}>{massImportFile.name}</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>
                    {(massImportFile.size / 1024 / 1024).toFixed(1)} MB — click to change
                  </div>
                </div>
              ) : (
                <div>
                  <div style={{ fontWeight: 600, marginBottom: '4px' }}>Drop your ZIP file here</div>
                  <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>or click to browse — .zip files only</div>
                </div>
              )}
              <input
                ref={massImportInputRef}
                type="file"
                accept=".zip"
                style={{ display: 'none' }}
                onChange={e => e.target.files[0] && setMassImportFile(e.target.files[0])}
              />
            </div>
            <div style={{ background: 'var(--bg-tertiary)', borderRadius: '8px', padding: '12px 14px', fontSize: '12px' }}>
              <div style={{ fontWeight: 600, marginBottom: '8px', color: 'var(--text-muted)' }}>Expected ZIP structure:</div>
              <pre style={{ margin: 0, fontFamily: 'monospace', color: 'var(--text-muted)', lineHeight: 1.7 }}>{`Collection Name (Poncho Split)/
└── Model Name (Eevee Vaporeon Poncho)/
    └── partfile.3mf

Zip the "Collection Name" folder and upload it.
Multiple zips with the same model names will be
merged automatically.`}</pre>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
